import { zodResolver } from '@hookform/resolvers/zod';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import type ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import type { LandingContent, Locale } from '../../content/types';
import { localizedPaths } from '../../lib/routes';
import { getRuntimeEnv } from '../../lib/env';
import { Button } from '../../components/Button';
import { contactSchema, type ContactFormValues } from './contactSchema';
import { submitContactForm } from './contactApi';

const ReCAPTCHAWidget = lazy(() => import('react-google-recaptcha'));

type ContactFormProps = {
  content: LandingContent['contact'];
  locale: Locale;
};

type SubmitStatus = 'idle' | 'sending' | 'success' | 'failure';

export function ContactForm({ content, locale }: ContactFormProps) {
  const runtimeEnv = useMemo(() => getRuntimeEnv(), []);
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const captchaRef = useRef<ReCAPTCHA | null>(null);
  const tokenRef = useRef('');

  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isSubmitting, touchedFields, isSubmitted },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      text: '',
      companyWebsite: '',
    },
  });

  const recaptchaErrorId = 'contact-recaptcha-error';

  useEffect(() => {
    if (captchaVisible) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      const timer = window.setTimeout(() => setCaptchaVisible(true), 0);
      return () => window.clearTimeout(timer);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setCaptchaVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '240px' },
    );

    const node = formRef.current;
    if (node) {
      observer.observe(node);
    }

    return () => observer.disconnect();
  }, [captchaVisible]);

  function resetCaptcha() {
    captchaRef.current?.reset();
    tokenRef.current = '';
    setToken('');
  }

  function setCaptchaToken(value: string) {
    tokenRef.current = value;
    setToken(value);
  }

  function validationMessage(error: { message?: string } | undefined): string | null {
    if (!error?.message) {
      return null;
    }

    const messages = {
      name: content.validation.name,
      email: content.validation.email,
      phone: content.validation.phone,
      textMin: content.validation.textMin,
      textMax: content.validation.textMax,
      token: content.validation.token,
    };

    return messages[error.message as keyof typeof messages] ?? error.message;
  }

  function visibleError(field: keyof ContactFormValues): string | null {
    const error = errors[field];
    const wasTouched = Boolean(touchedFields[field]);

    return wasTouched || isSubmitted ? validationMessage(error) : null;
  }

  const onSubmit = handleSubmit(async (values) => {
    setStatus('idle');
    const captchaToken = tokenRef.current;

    if (!captchaToken) {
      setError('root.recaptcha', { type: 'manual', message: 'token' });
      return;
    }

    try {
      setStatus('sending');
      await submitContactForm(
        {
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim(),
          text: values.text.trim(),
          token: captchaToken,
        },
        runtimeEnv.contactApiUrl,
      );
      reset();
      resetCaptcha();
      setStatus('success');
    } catch {
      resetCaptcha();
      setStatus('failure');
    }
  });

  const pending = isSubmitting || status === 'sending';
  const submitDisabled = pending || !token;
  const privacyPath = localizedPaths.dataProtection[locale];
  const recaptchaError = validationMessage(errors.root?.recaptcha);

  return (
    <form
      className="contact-form"
      ref={formRef}
      onSubmit={onSubmit}
      onFocusCapture={() => setCaptchaVisible(true)}
      noValidate
    >
      <div className="form-field">
        <label htmlFor="contact-name">{content.labels.name}</label>
        <input
          id="contact-name"
          type="text"
          autoComplete="name"
          placeholder={content.placeholders.name}
          aria-invalid={Boolean(visibleError('name'))}
          aria-describedby={visibleError('name') ? 'contact-name-error' : undefined}
          disabled={pending}
          {...register('name')}
        />
        <ErrorText id="contact-name-error" message={visibleError('name')} />
      </div>

      <div className="form-grid">
        <div className="form-field">
          <label htmlFor="contact-email">{content.labels.email}</label>
          <input
            id="contact-email"
            type="email"
            autoComplete="email"
            placeholder={content.placeholders.email}
            aria-invalid={Boolean(visibleError('email'))}
            aria-describedby={visibleError('email') ? 'contact-email-error' : undefined}
            disabled={pending}
            {...register('email')}
          />
          <ErrorText id="contact-email-error" message={visibleError('email')} />
        </div>

        <div className="form-field">
          <label htmlFor="contact-phone">{content.labels.phone}</label>
          <input
            id="contact-phone"
            type="tel"
            autoComplete="tel"
            placeholder={content.placeholders.phone}
            aria-invalid={Boolean(visibleError('phone'))}
            aria-describedby={visibleError('phone') ? 'contact-phone-error' : undefined}
            disabled={pending}
            {...register('phone')}
          />
          <ErrorText id="contact-phone-error" message={visibleError('phone')} />
        </div>
      </div>

      <div className="form-field">
        <label htmlFor="contact-text">{content.labels.text}</label>
        <textarea
          id="contact-text"
          rows={7}
          placeholder={content.placeholders.text}
          aria-invalid={Boolean(visibleError('text'))}
          aria-describedby={visibleError('text') ? 'contact-text-error' : undefined}
          disabled={pending}
          {...register('text')}
        />
        <ErrorText id="contact-text-error" message={visibleError('text')} />
      </div>

      <div className="honeypot" aria-hidden="true">
        <label htmlFor="company-website">Company website</label>
        <input
          id="company-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...register('companyWebsite')}
        />
      </div>

      <div className="captcha-area">
        {captchaVisible ? (
          <Suspense fallback={<div className="captcha-placeholder">Loading verification...</div>}>
            <ReCAPTCHAWidget
              ref={captchaRef}
              sitekey={runtimeEnv.recaptchaSiteKey}
              onChange={(value) => {
                setCaptchaToken(value ?? '');
                if (value) {
                  clearErrors('root.recaptcha');
                }
              }}
              onExpired={resetCaptcha}
              onErrored={resetCaptcha}
            />
          </Suspense>
        ) : (
          <button
            className="captcha-placeholder captcha-placeholder--button"
            type="button"
            onClick={() => setCaptchaVisible(true)}
          >
            Load verification
          </button>
        )}
        {recaptchaError ? (
          <p className="form-error" id={recaptchaErrorId}>
            {recaptchaError}
          </p>
        ) : null}
      </div>

      <p className="form-disclosure">
        <Link to={privacyPath}>{content.recaptchaDisclosure}</Link>
      </p>

      <Button
        type="submit"
        disabled={submitDisabled}
        className="contact-form__submit"
        aria-describedby={recaptchaError ? recaptchaErrorId : undefined}
      >
        {pending ? content.sending : content.submit}
      </Button>

      <div className="form-status" aria-live="polite" role="status">
        {status === 'success' ? <p className="form-status--success">{content.success}</p> : null}
        {status === 'failure' ? <p className="form-status--failure">{content.failure}</p> : null}
      </div>
    </form>
  );
}

function ErrorText({ id, message }: { id: string; message: string | null }) {
  if (!message) {
    return null;
  }

  return (
    <p className="form-error" id={id}>
      {message}
    </p>
  );
}
